import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { NotebookPen, Loader2, Plus } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LogRow = ({ log }) => (
  <div className="border-t border-slate-100 py-2.5 first:border-0" data-testid={`ext-log-row-${log.id}`}>
    <div className="flex items-center justify-between mb-0.5">
      <span className="text-xs font-medium text-slate-500">{log.conversationDate}</span>
      <span className="text-[11px] text-slate-400">{log.loggedByName}</span>
    </div>
    <p className="text-sm text-slate-700 whitespace-pre-line">{log.note}</p>
  </div>
);

/**
 * ExternalConversationLog — a manual note field for conversations that happened
 * OFF this integration (personal number / other channel). Not a message log.
 */
const ExternalConversationLog = ({ contextType, contextId }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);
  const [adding, setAdding] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/whatsapp/external-log`, { params: { contextType, contextId } });
      setLogs(Array.isArray(r.data) ? r.data : []);
    } catch { /* silent */ } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [contextType, contextId]);

  const save = async () => {
    if (!note.trim()) { toast.error('Enter a note'); return; }
    setSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/whatsapp/external-log`, {
        contextType, contextId, note: note.trim(), conversationDate: date,
      });
      toast.success('External conversation logged');
      setNote(''); setAdding(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to log'); }
    finally { setSaving(false); }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="external-conversation-log">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
          <NotebookPen className="h-4 w-4 text-slate-400" /> External conversation log
        </h3>
        {!adding && (
          <Button size="sm" variant="outline" onClick={() => setAdding(true)} data-testid="ext-log-add-btn">
            <Plus className="h-3.5 w-3.5 mr-1" /> Log a conversation
          </Button>
        )}
      </div>
      <p className="text-xs text-slate-400 mb-3">Record chats that happened outside this system (personal number, call, another channel).</p>

      {adding && (
        <div className="space-y-2 mb-3 p-3 rounded-lg bg-slate-50 border border-slate-200">
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Date</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-9 text-sm w-44" data-testid="ext-log-date" />
          </div>
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Note</Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="Summary of the conversation…" className="text-sm" data-testid="ext-log-note" />
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={save} disabled={saving} className="bg-slate-800 hover:bg-slate-900 text-white" data-testid="ext-log-save">
              {saving ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : null} Save note
            </Button>
            <Button size="sm" variant="ghost" onClick={() => { setAdding(false); setNote(''); }} data-testid="ext-log-cancel">Cancel</Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-sm text-slate-400 py-2">Loading…</div>
      ) : logs.length === 0 ? (
        <p className="text-sm text-slate-400 py-1">No external conversations logged yet.</p>
      ) : (
        <div data-testid="ext-log-list">
          {logs.map((l) => <LogRow key={l.id} log={l} />)}
        </div>
      )}
    </div>
  );
};

export default ExternalConversationLog;
